// Small bridge between the Auth0 context and non-React code (Apollo links,
// axios interceptors): who can mint a fresh access token, and how to report
// that the session is gone.

type TokenGetter = () => Promise<string>;

let getter: TokenGetter | null = null;
let expired = false;
const listeners = new Set<() => void>();

// Set by AuthProvider once the Auth0 client exists.
export const setTokenGetter = (fn: TokenGetter | null) => {
  getter = fn;
  expired = false;
};

// A fresh (cached or refreshed) access token; falls back to the last stored one
// if no getter is registered yet. Session loss is reported, not thrown.
export const getAccessToken = async (): Promise<string | null> => {
  if (!getter) return sessionStorage.getItem('token');
  try {
    return await getter();
  } catch (err) {
    const code = String((err as any)?.error || (err as any)?.message || '');
    // Only a dead session logs the user out; a network blip keeps the old token.
    if (/login_required|consent_required|invalid_grant|missing_refresh_token|missing refresh token/i.test(code)) {
      reportSessionExpired();
      return null;
    }
    return sessionStorage.getItem('token');
  }
};

// Fired at most once per session, however many requests fail at the same time.
export const reportSessionExpired = () => {
  if (expired) return;
  expired = true;
  listeners.forEach(fn => fn());
};

export const onSessionExpired = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

// Last user activity, persisted so the 30-minute idle rule also covers a closed
// tab: a stored login older than the limit is signed out on the next load.
export const LAST_ACTIVITY_KEY = 'lastActivityAt';

export const touchActivity = () => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch (e) {
    // storage can be blocked; the in-page idle timer still applies
  }
};

export const idleTooLong = (idleMs: number, now = Date.now()) => {
  try {
    const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return !!last && now - last > idleMs;
  } catch (e) {
    return false;
  }
};

export const clearActivity = () => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch (e) {
    // ignore
  }
};
